import omit from 'lodash/omit';
import pick from 'lodash/pick';

import {
  PermissionObjectType,
  PermissionType,
} from '@watershed/constants/permissions';
import {
  getLegacyPermissionFromResourceAndAction,
  RESOURCE_LEVEL_PERMISSION_TYPES,
} from '@watershed/shared-universal/permissions/permissionsConfig';
import {
  Action,
  ResourceSpecificPermissionType,
} from '@watershed/shared-universal/permissions/permissionTypes';
import assertNever from '@watershed/shared-util/src/assertNever';

import mapStrToEnum from '../utils/mapStrToEnum';
import {
  getPermissionOrigin,
  SourceObjectWithPermissionDelegate,
} from './permissionsHierarchy';

export type PermissionSelectionItem = {
  id?: string;
  permission: PermissionType;
  objectId?: string;
  objectType?: PermissionObjectType;
  removedBy?: string;
};

export type UserPermission = {
  permission: PermissionType;
  objectId?: string | null;
  objectType?: PermissionObjectType | null;
};

export type UserPermissions = Array<UserPermission>;

/**
 * Asserts the user has any of the required permissions
 */
export function hasPermission(
  userPermissions: Readonly<UserPermissions>,
  allowList: ReadonlyArray<PermissionType>,
  {
    allowAnyObject,
    source,
  }: {
    allowAnyObject?: boolean;
    source?: SourceObjectWithPermissionDelegate;
  } = {}
): boolean {
  return allowList.some((allowPermission) =>
    userPermissions.some(
      ({ permission, objectId }) =>
        !!getPermissionOrigin({
          currentPermission: {
            permission,
            objectId,
          },
          permissionToCheck: {
            permission: allowPermission,
            object: source,
          },
          allowAnyObject,
        })
    )
  );
}

// A permission item can either be object-specific or not, i.e. is there an
// object associated with it . For example, "Manage datasource X" and
// "Manage supplier Y" are object-specific, whereas "View reductions" is not.
export function isObjectSpecificPermission(
  permission: PermissionType
): boolean {
  switch (permission) {
    case PermissionType.ManageDataset:
    case PermissionType.ManageDatasource:
    case PermissionType.ApproveDatasource:
    case PermissionType.ApproveDataset:
    case PermissionType.ManageDisclosures:
    case PermissionType.ManageFund:
    case PermissionType.FinanceReadOnly:
    case PermissionType.EditReport:
    case PermissionType.ViewReport:
    case PermissionType.EditReportQuestionInstance:
    case PermissionType.ViewReportQuestionInstance:
    case PermissionType.ApproveFootprint:
    case PermissionType.ViewFootprint:
      return true;
    case PermissionType.Admin:
    case PermissionType.CorporateAdmin:
    case PermissionType.FinanceAdmin:
    case PermissionType.ViewEmployeeReport:
    case PermissionType.ManageMarketplacePurchases:
    case PermissionType.ManageMeasurement:
    case PermissionType.ManageCompanyTags:
    case PermissionType.ManageOrgHierarchy:
    case PermissionType.ManageReductionPlans:
    case PermissionType.ManageSingleSignOn:
    case PermissionType.ManageSuppliers:
    case PermissionType.ViewFootprintDetail:
    case PermissionType.ViewAuditDetail:
    case PermissionType.ViewLearningHub:
    case PermissionType.ViewReductions:
    case PermissionType.WatershedAdmin:
    case PermissionType.ViewCalculation:
    case PermissionType.EditCalculation:
    case PermissionType.ViewProductFootprint:
    case PermissionType.EditProductFootprint:
    case PermissionType.AnyUser:
      return false;
    default:
      // If we add a new permission type, make sure to update this.
      assertNever(permission);
  }
}

export function toPermissionSelectionItem(permission: {
  permission: PermissionType;
  objectId?: string | null;
}): PermissionSelectionItem {
  if (permission.objectId) {
    const resourcePermissionConfig =
      RESOURCE_LEVEL_PERMISSION_TYPES[
        permission.permission as ResourceSpecificPermissionType
      ];
    return {
      permission: permission.permission,
      objectId: permission.objectId,
      objectType: resourcePermissionConfig.resourceType,
    };
  } else {
    return {
      permission: permission.permission,
    };
  }
}

export const GlobalPermissions = pick(PermissionType, [
  PermissionType.Admin,
  PermissionType.WatershedAdmin,
]);
export const FinancePermissions = pick(PermissionType, [
  PermissionType.FinanceAdmin,
  PermissionType.ManageFund,
  PermissionType.FinanceReadOnly,
]);

export const CorporatePermissions = omit(PermissionType, [
  ...Object.values(GlobalPermissions),
  ...Object.values(FinancePermissions),
]);

export function userCanGrantPermission(
  userPermissions: UserPermissions,
  permission: PermissionType
): boolean {
  return hasPermission(userPermissions, [permission], {
    allowAnyObject: true,
  });
}

// Returns the permissions that a Watershed employee should have. The context
// here is that the permissions dialog is currently shared between dashboard
// and admin, and it should only show the permissions that the current user
// have permissions to grant. On dashboard, this is the logged-in user. On
// admin, we presume that the logged-in user is a Watershed employee.
export function getPermissionsForWatershedAdminUser(): UserPermissions {
  return [{ permission: PermissionType.WatershedAdmin }];
}

// For permissions for a value mapping-related object we hardcode the object name to be "Value mappings" for now
export const VALUE_MAPPING_OBJECT_NAME = 'Value mappings';

/**
 * TODO (tzv): Remove or add unit tests if we keep this function
 * Translates the current data structure of user permissions to the new format
 * which I think is more structured and intuitive but I may be wrong.
 */

export function permissionItemToPermissionId(
  permissionItem: UserPermission
): string {
  const resourcePermissionConfig =
    RESOURCE_LEVEL_PERMISSION_TYPES[
      permissionItem.permission as ResourceSpecificPermissionType
    ];
  if (resourcePermissionConfig) {
    return `${resourcePermissionConfig.resourceType}:${
      resourcePermissionConfig.action
    }:${permissionItem.objectId ?? 'all'}`;
  }
  return permissionItem.permission;
}

export function permissionIdToPermissionItem(
  permissionId: string
): UserPermission {
  const [permissionOrResourceType, action, objectId] = permissionId.split(':');
  if (action && objectId) {
    const objectType = mapStrToEnum(
      permissionOrResourceType,
      PermissionObjectType
    );
    const permission = getLegacyPermissionFromResourceAndAction(
      objectType,
      mapStrToEnum(action, Action)
    );
    if (objectId === 'all') {
      return { permission };
    }
    return {
      permission,
      objectType,
      objectId,
    };
  }
  return {
    permission: permissionOrResourceType as PermissionType,
  };
}
